/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import HidePasswordIcon from "assets/Icons/HidePassword";
import ShowPasswordIcon from "assets/Icons/ShowPassword";
import {
  FieldSet,
  FontAwesomeIcon,
  Input,
  Label,
  MyPhoneInput,
} from "components";
import { Button } from "components/Button";
import Heading from "components/Heading";
import { LoadingDots } from "components/Loaders";
import Stack from "components/Stack";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import useEventRouting from "hooks/useEventRouting";
import useTryCatch from "hooks/useTryCatch";
import {
  getSignUpFields,
  submitSignup,
  updateVisitorDetails,
} from "lib/register-link";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import ROUTES from "routes";
import { useOrgData } from "state/OrgDataContext";
import PageLayout from "templates/PageLayout";
import sha256 from "utils/encode";
import isValidEmail from "utils/isValidEmail";
import useLocalStorage from "utils/useLocalStorage";

import { pushToDataLayer } from "utils/gtm";

export default function SignUpPage({
  visitor: cachedVisitor,
  setVisitor: setCachedVisitor,
}) {
  const { t } = useTranslation();
  const { dataContent: EVENT_CONTENT } = useOrgData();
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const [isConfirmPasswordShown, setIsConfirmPasswordShown] = useState(false);

  const [eventCode] = useLocalStorage(LOCAL_STORAGE_KEYS.eventCode, null);
  const [token, setToken] = useLocalStorage(LOCAL_STORAGE_KEYS.token, null);
  const [, setIsActive] = useLocalStorage(LOCAL_STORAGE_KEYS.isActive, null);
  const [, setRegisterPageStage] = useLocalStorage(
    LOCAL_STORAGE_KEYS.registerPageStage,
    null,
  );

  const isUpdating = !!token;
  const { navigateWithEvent } = useEventRouting();
  const [errors, setErrors] = useState({});
  const [formFields, setFormFields] = useState();
  const [isLoadingFields, setIsLoadingFields] = useState(true);
  const [isLoading, setLoading] = useState(false);

  const getFields = useTryCatch(async () => {
    const api = await getSignUpFields(eventCode);
    if (api.ok) setFormFields(api.data);
  }, setIsLoadingFields);

  useEffect(() => {
    getFields();
  }, [token]);
  // useEffect(() => {
  //   const { isRouteMatching } = loginNavigator();
  //   if (isRouteMatching) {
  //     getFields();
  //   }
  // }, []);

  //   form state
  const [visitorData, setVisitorData] = useState({
    firstName: cachedVisitor?.firstName || "",
    lastName: cachedVisitor?.lastName || "",
    email: cachedVisitor?.email || "",
    phoneNumber: cachedVisitor?.phoneNumber || "",
    countryCode: cachedVisitor?.countryCode || EVENT_CONTENT.defaultCountryCode,
    password: "",
    confirmPassword: "",
  });
  const {
    firstName,
    lastName,
    email,
    phoneNumber,
    password,
    countryCode,
    confirmPassword,
  } = visitorData;
  const [passwordIsValid, setPasswordIsValid] = useState(true);
  const [showValidateBox, setIsValidateBoxOpen] = useState(false);

  const isFormValid = useRef({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirm_password: "",
  });

  // useEffect(()=> {
  //   // setErrors
  //   setFormValid({
  //     firstName: firstName?.length > 0,
  //     lastName: lastName?.length > 0,
  //     email: isValidEmail(email) && email?.length > 1,
  //     phoneNumber: phoneNumber?.length > 0,
  //     password: password?.length > 0 && passwordIsValid,
  //     confirm_password: confirmPassword === password,
  //   });
  // },[firstName, lastName, email, phoneNumber, password, confirmPassword])
  const handleOnSubmit = useTryCatch(async () => {
    pushToDataLayer({
      event: "register_click",
      screen_name: "registration",
      journey_step: "registration",
    });
    // // setErrors
    isFormValid.current = {
      firstName: firstName?.length > 0,
      lastName: lastName?.length > 0,
      email: isValidEmail(email) && email?.length > 1,
      phoneNumber: phoneNumber?.length > 0,
      password: password?.length > 0 && passwordIsValid,
      confirm_password: confirmPassword === password,
    };

    const nextErrors = {};
    if (!isFormValid.current.firstName)
      nextErrors.firstName = t("forms.validation.required");
    if (!isFormValid.current.lastName)
      nextErrors.lastName = t("forms.validation.required");
    if (!isFormValid.current.email)
      nextErrors.email = t("forms.validation.email");
    if (!isFormValid.current.password)
      nextErrors.password = t("forms.validation.required");
    if (!isFormValid.current.confirm_password)
      nextErrors.confirm_password = t("forms.validation.passwordMismatch");

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const errorMessages = Object.values(nextErrors).filter(Boolean);
      const firstErrorMessage =
        errorMessages[0] || t("forms.validation.required");
      return toast.error(firstErrorMessage);
    }
    const hashedPassword = await sha256(password);
    const payload = {
      event_token: eventCode,
      first_name: firstName,
      last_name: lastName,
      email: email,
      password: hashedPassword,
      country_code: countryCode,
      contact_number: phoneNumber,
    };
    let signUpAPI;
    if (isUpdating) {
      signUpAPI = await updateVisitorDetails(payload);
    } else {
      signUpAPI = await submitSignup(payload);
    }
    if (signUpAPI.ok) {
      setCachedVisitor({
        firstName: firstName,
        lastName: lastName,
        email: email,
        phoneNumber: phoneNumber,
        countryCode: countryCode,
      });
      // Batch localStorage updates and wait for completion
      await new Promise((resolve) => {
        if (signUpAPI.data.token) {
          setToken(signUpAPI.data.token);
        }
        setIsActive(false);
        setRegisterPageStage("verify");

        // Give localStorage time to update
        setTimeout(resolve, 100);
      });

      navigateWithEvent(ROUTES.registerLink.verifyLink);
    }
  }, setLoading);

  return (
    <PageLayout
      title={t("auth.register.title")}
      hasGoBack
      goBackMethod={() => {
        navigateWithEvent(ROUTES.registerLink.base);
      }}
    >
      <div className="mt-10 md:mt-16 flex flex-col -bg--theme-colors-surface-neutral-level02 -rounded-outer-container py-8 sm:py-s50 sm:px-10 px-8 lg:px-s50 xl:py-s60 xl:px-20 w-full lg:w-[700px] xl:w-[920px] space-y-10">
        {isLoadingFields ? (
          <div className="flex justify-center mt-[100px]">
            <LoadingDots />
          </div>
        ) : (
          <>
            <Stack>
              <Heading className="-text--theme-colors-text-title-brand" as="h2">
                {t("auth.register.signupTitle")}
              </Heading>
            </Stack>
            <Stack className="gap-y-10 grid md:grid-cols-2 sm:grid-cols-1 sm:gap-x-s0 md:gap-x-8">
              {formFields?.first_name?.is_visible && (
                <FieldSet isFullWidth className="relative">
                  <Input
                    error={errors?.firstName?.length > 0}
                    id="first_name"
                    name="first_name"
                    label={t("common.labels.firstName")}
                    value={firstName}
                    required
                    placeholder={t("guestInfoPage.firstNamePlaceholder")}
                    onChange={(e) => {
                      setVisitorData((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }));
                      if (errors?.firstName) {
                        setErrors((prev) => ({ ...prev, firstName: "" }));
                      }
                    }}
                  />
                  {errors?.firstName?.length > 0 && (
                    <span className="text-red-500 text-sm absolute -bottom-5 start-0">
                      {errors?.firstName}
                    </span>
                  )}
                </FieldSet>
              )}
              {formFields?.last_name?.is_visible && (
                <FieldSet isFullWidth className="relative">
                  <Input
                    error={errors?.lastName?.length > 0}
                    id="last_name"
                    name="last_name"
                    label={t("common.labels.lastName")}
                    required
                    value={lastName}
                    placeholder={t("guestInfoPage.lastNamePlaceholder")}
                    onChange={(e) => {
                      setVisitorData((prev) => ({
                        ...prev,
                        lastName: e.target.value,
                      }));
                      if (errors?.lastName) {
                        setErrors((prev) => ({ ...prev, lastName: "" }));
                      }
                    }}
                  />
                  {errors?.lastName?.length > 0 && (
                    <span className="text-red-500 text-sm absolute -bottom-5 start-0">
                      {errors?.lastName}
                    </span>
                  )}
                </FieldSet>
              )}
              {formFields?.email?.is_visible && (
                <FieldSet isFullWidth className="relative">
                  <Input
                    error={errors?.email?.length > 0}
                    required
                    id="email"
                    name="email"
                    label={t("common.labels.email")}
                    value={email}
                    placeholder={t("guestInfoPage.emailPlaceholder")}
                    onChange={(e) => {
                      setVisitorData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }));
                      if (errors?.email) {
                        setErrors((prev) => ({ ...prev, email: "" }));
                      }
                    }}
                  />
                  {errors?.email?.length > 0 && (
                    <span className="text-red-500 text-sm absolute -bottom-5 start-0">
                      {errors?.email}
                    </span>
                  )}
                </FieldSet>
              )}
              {formFields?.contact_number?.is_visible && (
                <FieldSet isFullWidth>
                  <Label htmlFor="phoneNumber">
                    {t("common.labels.phoneNumber")}
                  </Label>
                  <MyPhoneInput
                    isCodeMissing={errors?.countryCode?.length}
                    value={phoneNumber}
                    countryCode={countryCode}
                    onChange={(phoneNumber) => {
                      setVisitorData((prev) => ({
                        ...prev,
                        phoneNumber,
                      }));
                    }}
                    setCountryCode={(code) =>
                      setVisitorData((prev) => ({
                        ...prev,
                        countryCode: code,
                      }))
                    }
                    id="phoneNumber"
                  />
                </FieldSet>
              )}
              {formFields?.password?.is_visible && (
                <FieldSet isFullWidth className="relative">
                  <div className="relative">
                    {showValidateBox && (
                      <div className="p-[12px] bg-[white] shadow-lg shadow-[0px -1px 16px -4px rgba(27, 27, 27, 0.28)] absolute w-full top-[110%] flex-col space-y-s5 z-50">
                        <Heading as="h3" className="text-[14px]">
                          Password Strength Criteria
                        </Heading>
                        <div className="flex align-center items-center gap-x-2">
                          {password?.length >= 12 ? (
                            <FontAwesomeIcon
                              name="fa-check"
                              className="-text--theme-colors-success-default p-1"
                              size="sm"
                            />
                          ) : (
                            <FontAwesomeIcon
                              name="fa-xmark"
                              className="-text--theme-colors-danger-default p-1"
                            />
                          )}

                          <span
                            className={`text-[12px] ${
                              password?.length >= 12
                                ? "text-[#039855]"
                                : "text-[#E3655C]"
                            }`}
                          >
                            {t("auth.register.passwordInstruction.characters")}
                          </span>
                        </div>
                        <div className="flex align-center items-center gap-x-2">
                          {/[A-Z]/.test(password) ? (
                            <FontAwesomeIcon
                              name="fa-check"
                              className="-text--theme-colors-success-default p-1"
                              size="sm"
                            />
                          ) : (
                            <FontAwesomeIcon
                              name="fa-xmark"
                              className="-text--theme-colors-danger-default p-1"
                            />
                          )}

                          <span
                            className={`text-[12px] ${
                              /[A-Z]/.test(password)
                                ? "text-[#039855]"
                                : "text-[#E3655C]"
                            }`}
                          >
                            {t("auth.register.passwordInstruction.uppercase")}
                          </span>
                        </div>
                        <div className="flex align-center items-center gap-x-2">
                          {/[a-z]/.test(password) ? (
                            <FontAwesomeIcon
                              name="fa-check"
                              className="-text--theme-colors-success-default p-1"
                              size="sm"
                            />
                          ) : (
                            <FontAwesomeIcon
                              name="fa-xmark"
                              className="-text--theme-colors-danger-default p-1"
                            />
                          )}

                          <span
                            className={`text-[12px] ${
                              /[a-z]/.test(password)
                                ? "text-[#039855]"
                                : "text-[#E3655C]"
                            }`}
                          >
                            {t("auth.register.passwordInstruction.lowercase")}
                          </span>
                        </div>
                        <div className="flex align-center items-center gap-x-2">
                          {/[0-9]/.test(password) ? (
                            <FontAwesomeIcon
                              name="fa-check"
                              className="-text--theme-colors-success-default p-1"
                              size="sm"
                            />
                          ) : (
                            <FontAwesomeIcon
                              name="fa-xmark"
                              className="-text--theme-colors-danger-default p-1"
                            />
                          )}

                          <span
                            className={`text-[12px] ${
                              /[0-9]/.test(password)
                                ? "text-[#039855]"
                                : "text-[#E3655C]"
                            }`}
                          >
                            {t("auth.register.passwordInstruction.number")}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="relative" test-id="vX8mSaxM">
                      <Input
                        name="password"
                        id="password"
                        type={isPasswordShown ? "text" : "password"}
                        required
                        label={t("common.labels.password")}
                        value={password}
                        error={!passwordIsValid}
                        placeholder={t("guestInfoPage.passwordPlaceholder")}
                        onChange={(e) => {
                          const passwordVal = e.target.value;
                          let progress = 0;
                          if (passwordVal.length >= 12) {
                            progress += 25;
                          }
                          const regexTests = [/[A-Z]/, /[0-9]/, /[a-z]/];
                          regexTests.forEach((regex) => {
                            if (regex.test(passwordVal)) {
                              progress += 25;
                            }
                          });
                          if (progress === 100) {
                            setPasswordIsValid(true);
                          } else {
                            setPasswordIsValid(false);
                          }
                          setVisitorData((prev) => ({
                            ...prev,
                            password: passwordVal,
                          }));
                          if (progress !== 100) {
                            setIsValidateBoxOpen(true);
                          } else {
                            setIsValidateBoxOpen(false);
                          }
                          if (errors?.password) {
                            setErrors((prev) => ({ ...prev, password: "" }));
                          }
                        }}
                        onFocus={() => {
                          if (!passwordIsValid) {
                            setIsValidateBoxOpen(true);
                          }
                        }}
                        onBlur={(e) => {
                          setIsValidateBoxOpen(false);
                        }}
                      />
                      <div
                        className="absolute end-2 inset-y-0 flex items-center bg-secondary-accent h-fit p-1 top-[40px] rounded-full"
                        onClick={() => setIsPasswordShown(!isPasswordShown)}
                        test-id="Qho4aFeQ"
                      >
                        {isPasswordShown ? (
                          <HidePasswordIcon test-id="m69s0Z9r" />
                        ) : (
                          <ShowPasswordIcon test-id="ZOAkh95C" />
                        )}
                      </div>
                    </div>
                  </div>
                  {errors?.password?.length > 0 && (
                    <span className="text-red-500 text-sm absolute -bottom-5 start-0">
                      {errors?.password}
                    </span>
                  )}
                </FieldSet>
              )}
              {formFields?.confirm_password?.is_visible && (
                <FieldSet isFullWidth>
                  <div className="relative" test-id="vX8mSaxM">
                    <Input
                      name="confirmPassword"
                      id="confirmPassword"
                      label={t("common.labels.confirmPassword")}
                      required
                      type={isConfirmPasswordShown ? "text" : "password"}
                      value={confirmPassword}
                      error={confirmPassword !== password}
                      placeholder={t(
                        "guestInfoPage.confirmPasswordPlaceholder",
                      )}
                      onChange={(e) => {
                        setVisitorData((prev) => ({
                          ...prev,
                          confirmPassword: e.target.value,
                        }));
                      }}
                    />
                    <div
                      className="absolute end-2 inset-y-0 flex items-center bg-secondary-accent h-fit p-1 top-[40px] rounded-full"
                      onClick={() =>
                        setIsConfirmPasswordShown(!isConfirmPasswordShown)
                      }
                      test-id="Qho4aFeQ"
                    >
                      {isConfirmPasswordShown ? (
                        <HidePasswordIcon test-id="m69s0Z9r" />
                      ) : (
                        <ShowPasswordIcon test-id="ZOAkh95C" />
                      )}
                    </div>
                  </div>
                </FieldSet>
              )}
            </Stack>
            <Button
              isLoading={isLoading}
              variant="primary"
              onClick={handleOnSubmit}
              className="!mt-[40px]"
              childClassName="flex items-center gap-x-s10 justify-center font-bold h-full"
            >
              <span className="text-[18px]">{t("common.buttons.submit")}</span>
              <FontAwesomeIcon name="fa-chevron-right" />
            </Button>
          </>
        )}
      </div>
    </PageLayout>
  );
}
